/**
 * ContactInfoItem — one row of the marketing contact-info panel
 * (decorative icon + label + value).
 *
 * Extracted from `<ContactPage>` to cut the page's JSX nesting depth
 * (`web/render-method-complexity`): the panel's branch ran
 * section → div.container → div.contact-grid → div.contact-info →
 * div.contact-info-item → div → p (depth 7, threshold 6). Rendering the same
 * markup here keeps the DOM byte-identical while dropping the page to depth 5.
 *
 * The icon stays a caller-supplied prop (rather than being inlined here) so
 * the emoji literals remain in the page, where the existing
 * `web/no-hardcoded-strings` cover already accounts for them — this component
 * needs no suppression of its own.
 *
 * @module portals/marketing-website/components/ContactInfoItem
 */

import PropTypes from 'prop-types';

/**
 * @param {Object} props
 * @param {string} props.icon - Decorative emoji glyph (rendered aria-hidden).
 * @param {string} props.label - Translated label.
 * @param {string} props.value - Translated value.
 * @returns {React.JSX.Element}
 */
export default function ContactInfoItem({ icon, label, value }) {
  return (
    <div className="contact-info-item">
      <span className="contact-info-icon" aria-hidden="true">
        {icon}
      </span>
      <div>
        <p className="contact-info-label">{label}</p>
        <p className="contact-info-value">{value}</p>
      </div>
    </div>
  );
}

ContactInfoItem.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};
